import axios, {type AxiosResponse} from "axios";
import {createClient} from "@connectrpc/connect";
import {apiTransport} from "api/connectTransport";
import type {AssertNoSpecDrift, RequestSpecDrift, Schemas, SpecDrift} from "api/schemaAssert";
import {SponsorAdminService, type OperatorBooking} from "gen/kitecorp/transferify/admin/v1/sponsor_admin_pb";
import type {ContractDocumentIdentity} from "pay/checkoutDocument";
import type {BookableTemplateKey, SponsorCategory, SponsorOptions} from "./sponsorBooking";
import type {SponsorAvailability} from "./sponsorCalendar";
import {sponsorAdminBookingOf} from "./sponsorAdminMessages";

/**
 * The sponsor endpoints (api#420 / ui#496, reworked for epic #441).
 *
 * Three admission tiers, all decided by the server:
 *  - ANONYMOUS — `GET /sponsor/availability`. The shop window: anybody may see the calendar and the rate
 *    without an account. The api's anonymous `GET /sponsor/takeover` has no reader here since no page shows a
 *    paid takeover (kitecorp/transferify-ui#2123, #3649).
 *  - AUTHENTICATED — `POST /sponsor/assets`, `POST /sponsor/bookings` and `GET /sponsor/bookings`.
 *    Sponsors are account holders of ANY tier since epic #441, so these answer 401 to a stranger.
 *  - the `operate` action: `SponsorAdminService`, the operator's review queue (kitecorp/transferify-ui#4024).
 *
 * This module carries no authorization logic of its own: a client that decided who may book, or who is
 * an operator, would be a second and weaker copy of a server rule. The ui's job is only to route an
 * anonymous visitor to sign-in BEFORE the 401 (see `useSponsorBooking`), never to grant anything.
 */

/** `GET /sponsor/availability`. */
export interface SponsorAvailabilityDto extends SponsorAvailability {
	dailyRateMinor: number;
	currency: string;
}

/**
 * The booking lifecycle, as the api spells it. Named once and referenced by every payload that carries
 * a state, so the advertiser's view and the operator's view cannot drift to different vocabularies —
 * and so a new server state is one compile error here rather than several silent gaps.
 *
 * Reshaped by kitecorp/transferify-api-service#1362: money moves AFTER approval, so `PENDING_PAYMENT`
 * (submitted and unpaid) is gone and two states take its place. `AWAITING_PAYMENT` is approved and
 * unpaid. It holds the advertiser's dates and serves nothing, and `EXPIRED` is what an unpaid
 * approval becomes when its payment window closes, releasing those dates. The api's migration moves
 * every old `PENDING_PAYMENT` row to `PENDING_REVIEW`, so no booking arrives here in a state this
 * union cannot name.
 */
export type SponsorBookingState =
	| "PENDING_REVIEW"
	| "AWAITING_PAYMENT"
	| "APPROVED"
	| "ACTIVE"
	| "ENDED"
	| "REJECTED"
	| "EXPIRED"
	| "WITHDRAWN";

/**
 * What a creative IS. Mirrors the api's `SponsorAssetRole`, and travels as `POST /sponsor/assets?role=`.
 *
 * `IMAGE` is the api's own default and is sent explicitly all the same: the query parameter is the only
 * thing that tells the two stores apart, and a default relied upon is a default that changes silently.
 *
 * `VIDEO` and `POSTER` are kitecorp/transferify-api-service#1141's: the VIDEO_LOOP treatment's own
 * creative and the still frame that stands in for it.
 */
export type SponsorAssetRole = "IMAGE" | "LOGO" | "VIDEO" | "POSTER";

/**
 * `POST /sponsor/bookings`. Brand, price and currency are all derived server-side.
 *
 * `options` is OMITTED rather than sent as null when the booking sets none, and that is the request
 * schema's own shape: `SponsorBookingRequest.options` is a plain `$ref`, so the documented contract has
 * no null for it, while the api's `@Nullable` binding treats an absent key and a null one identically.
 * Sending the key with a null would be a value the published contract does not describe.
 *
 * The VIDEO and POSTER asset ids arrived with kitecorp/transferify-api-service#1141 and are sent as
 * `null` rather than omitted, exactly as `logoAssetId` is: all three are `@Nullable` uuids on the
 * request record and a booking that claims none has to say so once. `assetIds` travels as an empty
 * array for a video booking, which the same change made legal (its `minItems` is 0 now).
 */
export interface SponsorBookingRequestBody {
	buyerDeclaration?: {consumer: boolean; earlyStartRequested: boolean; locale: "EN" | "RO" | "DE" | "FR"};
	/**
	 * The registered Advertising Terms document the form linked when the advertiser ticked the box
	 * (kitecorp/transferify-api-service#2149): its version, language and digest, as `GET
	 * /sponsor/advertising-terms` announced them. Omitted, never null, when the api announced no document
	 * or could not be asked; the api then records none for the booking rather than today's version.
	 */
	advertisingTerms?: ContractDocumentIdentity;
	template: BookableTemplateKey;
	options?: SponsorOptions;
	sponsorName: string;
	sponsorEmail: string;
	headline: string;
	tagline: string | null;
	ctaLabel: string;
	ctaUrl: string;
	assetIds: string[];
	logoAssetId: string | null;
	videoAssetId: string | null;
	posterAssetId: string | null;
	startsOn: string;
	endsOn: string;
}

/**
 * `POST /sponsor/bookings`. The booking that was created.
 *
 * `checkoutUrl` is ALWAYS null since kitecorp/transferify-api-service#1362: a submission takes no
 * money. It is typed and kept rather than deleted because the field is still on the wire. The api
 * held it so a client written against the old shape keeps parsing this response across the deploy,
 * and a ui that silently dropped it would give a future reader no clue that submitting once redirected
 * to Stripe. Nothing reads the value; {@link SponsorApi.startCheckout} is where a payment begins now.
 */
export interface SponsorBookingCreated {
	bookingId: string;
	checkoutUrl: string | null;
}

/** `POST /sponsor/bookings/{bookingId}/checkout`. The Stripe-hosted page to send the advertiser to. */
export interface SponsorCheckout {
	checkoutUrl: string;
}

export interface SponsorAssetUploaded {
	assetId: string;
	url: string;
}

/**
 * `GET /sponsor/bookings` — one row of the SIGNED-IN advertiser's own bookings, newest first (#441).
 *
 * Deliberately narrower than {@link SponsorAdminBooking}: the query is scoped to the caller, so there
 * is no `brandKey` to disclose. Optional consumer-withdrawal details identify the booking's
 * acknowledgement address for its owner to confirm. `rejectionReason` is the operator's words, shown verbatim and never paraphrased, because it is
 * the only explanation the advertiser will ever get for a refund.
 */
export interface MyBooking {
	consumerWithdrawal?: {name: string; email: string};
	bookingId: string;
	state: SponsorBookingState;
	/* Both of the advertiser's categories since kitecorp/transferify-api-service#1140: their own list has to
	   name the wallpaper they bought, and a backdrop-only union here would have typed it as a backdrop. Never a
	   scene: the api refuses that category at booking, so this is narrower than the served enum on purpose. */
	template: BookableTemplateKey;
	headline: string;
	startsOn: string;
	endsOn: string;
	amountMinor: number;
	currency: string;
	impressions: number;
	rejectionReason: string | null;
	/**
	 * When this booking's payment window closes, as a local date-time string, or `null` before approval
	 * (kitecorp/transferify-api-service#1362).
	 *
	 * Set from approval onward. It survives into `APPROVED` and beyond, so it is NEVER on its own the
	 * question "may this be paid for". `state === "AWAITING_PAYMENT"` is that question; this is only the
	 * deadline to show beside it.
	 */
	paymentDueAt: string | null;
	imageUrls: string[];
}

/** The operator's view. Carries the sponsor's contact and price, which the public payloads never do. */
export interface SponsorAdminBooking {
	bookingId: string;
	brandKey: string;
	state: SponsorBookingState;
	template: BookableTemplateKey;
	/**
	 * Which stage this booking composes on (kitecorp/transferify-ui#1870).
	 *
	 * Advisory here exactly as it is on the takeover payload: `toSponsorTakeover` DERIVES the category
	 * from the template and refuses a row whose two halves disagree, rather than composing a full-page
	 * picture into the card-side region. Optional because an api predating
	 * kitecorp/transferify-api-service#1140 sends none, and the operator page must render such a row the
	 * way it always did rather than blanking it.
	 */
	category?: SponsorCategory;
	/**
	 * The advertiser's per-booking knobs, or `null` when they set none.
	 *
	 * Declared since kitecorp/transferify-ui#1870, and the operator's review pane is why: the row is fed
	 * straight to `toSponsorTakeover`, so without this field a reviewer was shown every wallpaper laid
	 * out the DEFAULT way rather than the way it was booked — a wipe sweeping the wrong direction, copy
	 * in the wrong corner, in the brand's colour or not. The queue also lists these in words, which is
	 * the half a picture cannot carry.
	 */
	options?: SponsorOptions | null;
	/** The advertiser's brand mark, drawn in the poster's corner (kitecorp/transferify-ui#1866). */
	logoUrl: string | null;
	sponsorName: string;
	sponsorEmail: string;
	headline: string;
	tagline: string | null;
	ctaLabel: string;
	ctaUrl: string;
	startsOn: string;
	endsOn: string;
	amountMinor: number;
	currency: string;
	impressions: number;
	rejectionReason: string | null;
	/** The payment deadline an approved booking is holding its dates under; `null` before approval. */
	paymentDueAt: string | null;
	/**
	 * When we recorded that this booking's money has to go back, or `null` on every ordinary booking
	 * (kitecorp/transferify-api-service#1362, surfaced by kitecorp/transferify-ui#2152).
	 *
	 * The OPERATOR record alone carries it — `MyBooking` deliberately does not — because an advertiser
	 * must not read "refund due" as a refund that has already been sent.
	 *
	 * It is the moment the debt was RECOGNIZED, which is when the payment reached us, and it is not the
	 * Stripe session's own paid time: the api holds that as `paidAt` and does not serve it here. The
	 * queue therefore labels it as a recorded payment rather than as the advertiser's paid date, and an
	 * operator searches Stripe around it. `OperatorBooking` carries no `checkoutSessionId` either, so the
	 * booking id is the reference the queue shows — it travels on the session as `client_reference_id`
	 * and as the `transferify_sponsor_booking_id` metadata key, so it does find the payment.
	 */
	refundDueAt: string | null;
	imageUrls: string[];
	/**
	 * A booking's VIDEO creative: the looping video and the still frame shown in its place
	 * (kitecorp/transferify-ui#1867, kitecorp/transferify-ui#1870).
	 *
	 * Declared rather than left to untyped pass-through, and the operator's review pane is why: it feeds
	 * this row straight to `toSponsorTakeover`, which refuses a video booking missing either url.
	 * Undeclared, the two survived the hop only because nothing narrowed the object — so the preview
	 * worked by accident, and a field renamed server-side would have blanked it with nothing to say why.
	 *
	 * `AdminSponsorsPage` reads them DIRECTLY as well, and gates its review player on their presence rather than
	 * on the template. That is not redundancy with the preview above: the api serves both urls for any
	 * booking made against a video treatment, while whether THIS bundle can animate that treatment is a
	 * separate question with a separate answer — the two parted company for the whole window between
	 * this treatment's revert and its return, and an operator still had to review the file that was paid
	 * for. Reading the fields keeps that true whichever way the catalogue moves next.
	 *
	 * A field renamed in the contract is a `tsc` error in `sponsorAdminMessages.ts`, which reads the generated
	 * `OperatorBooking`.
	 */
	videoUrl: string | null;
	posterUrl: string | null;
}

/*
 * OpenAPI conformance (#208): `tsc -b` fails when these drift from the generated spec types. Every
 * payload carrying a `template` is bound, so a treatment added or withdrawn server-side is a compile
 * error here rather than a silent mismatch on the takeover surface (ui#585). `SponsorTakeoverDto` is
 * bound where it is declared, in `takeoverModel`.
 */
export type _SponsorAvailabilityDto = AssertNoSpecDrift<SpecDrift<SponsorAvailabilityDto, Schemas["Availability"]>>;
/*
 * `advertisingTerms` is exempted for the third kind of reason `focalPoint` once was: a field this ui
 * declares AHEAD of the committed snapshot, not a springdoc bug. The backend source of truth is
 * `SponsorBookingRequest.advertisingTerms` in kitecorp/transferify-api-service (#2149), a nullable
 * `ContractDocumentSelection` the api ignores when absent. The exemption is removed by the schema sync
 * that follows the api's deploy to DEV (`npm run api:fetch-spec && npm run api:generate`, or the
 * `chore(api): sync deployed DEV schema` pull request), once `api/openapi.json` names the key.
 */
export type _SponsorBookingRequestBody = AssertNoSpecDrift<
	RequestSpecDrift<SponsorBookingRequestBody, Schemas["SponsorBookingRequest"], "advertisingTerms">>;
/*
 * The pay-after-approval deploy window (kitecorp/transferify-ui#2147) is CLOSED.
 *
 * While it was open these bindings exempted `checkoutUrl`, `state` and `paymentDueAt`, because this ui
 * shipped ahead of kitecorp/transferify-api-service#1362 and the committed snapshot was still the old
 * contract. The api's schema-sync workflow has since committed the new one: `api/openapi.json` spells
 * the state enum with `AWAITING_PAYMENT`/`EXPIRED`, carries `paymentDueAt` and `refundDueAt`, types
 * `checkoutUrl` as nullable, and describes the `Checkout` schema that `SponsorCheckout` was left
 * unbound for. The three exemptions are therefore gone and that binding exists: an exemption outliving
 * its reason is the drift guard switched off for exactly the fields that were most recently moving.
 */
export type _SponsorBookingCreated =
	AssertNoSpecDrift<SpecDrift<SponsorBookingCreated, Schemas["BookingCreated"]>>;
export type _SponsorAssetUploaded = AssertNoSpecDrift<SpecDrift<SponsorAssetUploaded, Schemas["Asset"]>>;
export type _MyBooking = AssertNoSpecDrift<SpecDrift<MyBooking, Schemas["MyBooking"]>>;
export type _SponsorCheckout = AssertNoSpecDrift<SpecDrift<SponsorCheckout, Schemas["Checkout"]>>;

export interface SponsorWithdrawalReceipt {
	bookingId: string;
	receivedAt: string;
	refundDueBy: string;
	email: string;
	declaration: string;
}

export type _SponsorWithdrawalReceipt = AssertNoSpecDrift<SpecDrift<SponsorWithdrawalReceipt, Schemas["Receipt"]>>;

function operatorQueue() {
	return createClient(SponsorAdminService, apiTransport);
}

/** The booking a review answered with; an answer without one is a malformed answer, never a review that happened. */
function reviewed(answer: {booking?: OperatorBooking}): SponsorAdminBooking {
	if (answer.booking === undefined) {
		throw new Error("The review answered without the booking");
	}
	return sponsorAdminBookingOf(answer.booking);
}

export class SponsorApi {
	static async withdraw(bookingId: string, name: string): Promise<SponsorWithdrawalReceipt> {
		const {data} = await axios.post<SponsorWithdrawalReceipt>(
			`/sponsor/bookings/${bookingId}/withdrawal`, {name} satisfies Schemas["SponsorWithdrawalRequest"]);
		return data;
	}

	static async getAvailability(): Promise<SponsorAvailabilityDto> {
		const {data} = await axios.get<unknown, AxiosResponse<SponsorAvailabilityDto>>(
			"/sponsor/availability");
		return data;
	}

	/**
	 * Stores one creative and returns the id a submission will claim it by.
	 *
	 * The form field stays `image` for every role, including the video: it is the multipart PART NAME the
	 * api's controller binds, not a statement about the bytes, and the api derives the stored type from
	 * the payload itself in every case.
	 */
	static async uploadAsset(
		file: File, role: SponsorAssetRole = "IMAGE",
	): Promise<SponsorAssetUploaded> {
		const body = new FormData();
		body.append("image", file);
		const {data} = await axios.post<unknown, AxiosResponse<SponsorAssetUploaded>>(
			"/sponsor/assets", body,
			{params: {role}, headers: {"Content-Type": "multipart/form-data"}});
		return data;
	}

	static async createBooking(request: SponsorBookingRequestBody): Promise<SponsorBookingCreated> {
		const {data} = await axios.post<unknown, AxiosResponse<SponsorBookingCreated>>(
			"/sponsor/bookings", request);
		return data;
	}

	/**
	 * Opens Stripe Checkout for an approved, unpaid booking the caller owns
	 * (kitecorp/transferify-api-service#1362).
	 *
	 * 409 `SPONSOR_NOT_PAYABLE` when the booking is not approved-and-unpaid or its window has closed,
	 * and 404 for a booking belonging to somebody else. The api answers ONE code for both refusals
	 * because the advertiser's next move is the same either way: look at what the booking's own row now
	 * says. `myBookings.ts` is where that sentence is chosen.
	 */
	static async startCheckout(bookingId: string): Promise<SponsorCheckout> {
		const {data} = await axios.post<unknown, AxiosResponse<SponsorCheckout>>(
			`/sponsor/bookings/${bookingId}/checkout`);
		return data;
	}

	/** The caller's own bookings, newest first. 401 for a stranger — never an empty list. */
	static async getMyBookings(): Promise<MyBooking[]> {
		const {data} = await axios.get<unknown, AxiosResponse<MyBooking[]>>("/sponsor/bookings");
		return data;
	}

	static async getPendingQueue(): Promise<SponsorAdminBooking[]> {
		const {bookings} = await operatorQueue().listPendingBookings({});
		return bookings.map(sponsorAdminBookingOf);
	}

	static async getSchedule(from: string, to: string): Promise<SponsorAdminBooking[]> {
		const {bookings} = await operatorQueue().getSponsorSchedule({from, to});
		return bookings.map(sponsorAdminBookingOf);
	}

	static async approve(bookingId: string): Promise<SponsorAdminBooking> {
		return reviewed(await operatorQueue().approveBooking({bookingId}));
	}

	static async reject(bookingId: string, reason: string): Promise<SponsorAdminBooking> {
		return reviewed(await operatorQueue().rejectBooking({bookingId, reason}));
	}
}
